/**
 * Hand-curated minimal ABIs for the functions the frontend actually calls.
 * Trimmed from forge artifacts to keep bundle small.
 */

export const PIGGY_ORIGINALS_ABI = [
    { type: "function", name: "mint",            stateMutability: "payable", inputs: [{ name: "quantity", type: "uint256" }], outputs: [] },
    { type: "function", name: "reveal",          stateMutability: "nonpayable", inputs: [
        { name: "tokenIds", type: "uint256[]" },
        { name: "rarities", type: "uint8[]" },
        { name: "proofs", type: "bytes32[][]" },
    ], outputs: [] },
    { type: "function", name: "rarityRoot",      stateMutability: "view",       inputs: [], outputs: [{ type: "bytes32" }] },
    { type: "function", name: "mintPrice",       stateMutability: "view",    inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "perWalletCap",    stateMutability: "view",    inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "totalMinted",     stateMutability: "view",    inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "MAX_SUPPLY",      stateMutability: "view",    inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "mintActive",      stateMutability: "view",    inputs: [], outputs: [{ type: "bool" }] },
    { type: "function", name: "mintedBy",        stateMutability: "view",    inputs: [{ name: "wallet", type: "address" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "balanceOf",       stateMutability: "view",    inputs: [{ name: "owner", type: "address" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "tokenOfOwnerByIndex", stateMutability: "view", inputs: [{ name: "owner", type: "address" }, { name: "index", type: "uint256" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "rarityOf",        stateMutability: "view",    inputs: [{ name: "tokenId", type: "uint256" }], outputs: [{ type: "uint8" }] },
    { type: "function", name: "totalBoostBps",   stateMutability: "view",    inputs: [{ name: "owner", type: "address" }], outputs: [{ type: "uint32" }] },
] as const;

export const REFERRAL_REGISTRY_ABI = [
    { type: "function", name: "registerCode",   stateMutability: "nonpayable", inputs: [{ name: "code", type: "bytes12" }], outputs: [] },
    { type: "function", name: "attribute",      stateMutability: "nonpayable", inputs: [{ name: "code", type: "bytes12" }], outputs: [] },
    { type: "function", name: "claim",          stateMutability: "nonpayable", inputs: [], outputs: [] },
    { type: "function", name: "codeOwner",      stateMutability: "view",       inputs: [{ name: "code", type: "bytes12" }], outputs: [{ type: "address" }] },
    { type: "function", name: "referrerCode",   stateMutability: "view",       inputs: [{ name: "referrer", type: "address" }], outputs: [{ type: "bytes12" }] },
    { type: "function", name: "attributionOf",  stateMutability: "view",       inputs: [{ name: "referee", type: "address" }], outputs: [{ type: "address" }] },
    { type: "function", name: "referrerVolume", stateMutability: "view",       inputs: [{ name: "referrer", type: "address" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "referrerBuyCount", stateMutability: "view",     inputs: [{ name: "referrer", type: "address" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "earned",         stateMutability: "view",       inputs: [{ name: "referrer", type: "address" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "previewReferralMultiplier", stateMutability: "view", inputs: [{ name: "referrer", type: "address" }], outputs: [{ type: "uint32" }] },
] as const;

export const PIGGY_TOKEN_ABI = [
    { type: "function", name: "balanceOf",  stateMutability: "view", inputs: [{ name: "owner", type: "address" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "totalSupply", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "decimals",   stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
    { type: "function", name: "allowance",  stateMutability: "view", inputs: [{ name: "owner", type: "address" }, { name: "spender", type: "address" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "approve",    stateMutability: "nonpayable", inputs: [{ name: "spender", type: "address" }, { name: "amount", type: "uint256" }], outputs: [{ type: "bool" }] },
    // Live tax rates — sell tax is elevated during the 48h launch glide, so
    // quotes must read the current value rather than assume steady-state 4%.
    { type: "function", name: "currentSellTaxBps", stateMutability: "view", inputs: [], outputs: [{ type: "uint16" }] },
    { type: "function", name: "currentBuyTaxBps",  stateMutability: "view", inputs: [], outputs: [{ type: "uint16" }] },
] as const;

export const PIGGY_STAKING_ABI = [
    { type: "function", name: "stake",       stateMutability: "nonpayable", inputs: [{ name: "amount", type: "uint256" }], outputs: [] },
    { type: "function", name: "unstake",     stateMutability: "nonpayable", inputs: [{ name: "amount", type: "uint256" }], outputs: [] },
    { type: "function", name: "claim",       stateMutability: "nonpayable", inputs: [], outputs: [] },
    { type: "function", name: "exit",        stateMutability: "nonpayable", inputs: [], outputs: [] },
    { type: "function", name: "totalStaked", stateMutability: "view",       inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "globalIndex", stateMutability: "view",       inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "earned",      stateMutability: "view",       inputs: [{ name: "user", type: "address" }], outputs: [{ type: "uint256" }] },
    // NOTE: must match the deployed struct exactly. The reward audit widened
    // these to uint256 and dropped the __reserved slot (3 fields, not 4). A
    // stale 4x uint128 layout makes viem fail with "could not decode output",
    // which silently zeroes the displayed stake — the bug that made staked
    // PIGGY look "gone".
    { type: "function", name: "userStateOf", stateMutability: "view",       inputs: [{ name: "user", type: "address" }], outputs: [{
        type: "tuple",
        components: [
            { name: "stakedAmount",    type: "uint256" },
            { name: "indexCheckpoint", type: "uint256" },
            { name: "pendingClaim",    type: "uint256" },
        ],
    }]},
    { type: "function", name: "totalReceived", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
] as const;

export const LOCKED_LP_STAKING_ABI = [
    { type: "function", name: "stake",     stateMutability: "nonpayable", inputs: [{ name: "amount", type: "uint256" }, { name: "tier", type: "uint8" }], outputs: [] },
    { type: "function", name: "addStake",  stateMutability: "nonpayable", inputs: [{ name: "amount", type: "uint256" }], outputs: [] },
    { type: "function", name: "unstake",   stateMutability: "nonpayable", inputs: [{ name: "amount", type: "uint256" }], outputs: [] },
    { type: "function", name: "claim",     stateMutability: "nonpayable", inputs: [], outputs: [] },
    { type: "function", name: "syncMultiplier", stateMutability: "nonpayable", inputs: [{ name: "user", type: "address" }], outputs: [] },
    { type: "function", name: "totalStaked",     stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "totalWeightedStake", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "globalIndex",     stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
    { type: "function", name: "earned",          stateMutability: "view", inputs: [{ name: "user", type: "address" }], outputs: [{ type: "uint256" }] },
    { type: "function", name: "previewEffectiveMultiplier", stateMutability: "view", inputs: [{ name: "user", type: "address" }], outputs: [{ type: "uint32" }] },
    { type: "function", name: "lpToken",         stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
    // `amount` widened to uint256 by the reward audit (the other fields and the
    // __reserved tier slot are unchanged). Same word count as before, so this
    // one still decoded — updated to match the contract exactly regardless.
    { type: "function", name: "userStakeOf",     stateMutability: "view", inputs: [{ name: "user", type: "address" }], outputs: [{
        type: "tuple",
        components: [
            { name: "amount",         type: "uint256" },
            { name: "lockEnd",        type: "uint64" },
            { name: "multiplierBps",  type: "uint32" },
            { name: "__reserved",     type: "uint32" },
        ],
    }]},
    { type: "function", name: "lockDuration",    stateMutability: "view", inputs: [{ name: "tier", type: "uint8" }], outputs: [{ type: "uint32" }] },
    { type: "function", name: "lockMultiplierBps", stateMutability: "view", inputs: [{ name: "tier", type: "uint8" }], outputs: [{ type: "uint32" }] },
    { type: "function", name: "totalReceived",   stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
] as const;

export const LOCK_TIER_LABELS = ["No lock", "30 days", "90 days", "180 days"] as const;
export const LOCK_TIER_MULTIPLIERS = ["1.0×", "1.2×", "1.5×", "2.0×"] as const;
export const LOCK_TIER_DURATIONS_SEC = [0, 30 * 86400, 90 * 86400, 180 * 86400] as const;

export const DEX_ROUTER_ABI = [
    {
        type: "function", name: "getAmountsOut", stateMutability: "view",
        inputs: [
            { name: "amountIn", type: "uint256" },
            { name: "path",     type: "address[]" },
        ],
        outputs: [{ name: "amounts", type: "uint256[]" }],
    },
    {
        type: "function", name: "swapExactETHForTokensSupportingFeeOnTransferTokens",
        stateMutability: "payable",
        inputs: [
            { name: "amountOutMin", type: "uint256" },
            { name: "path",         type: "address[]" },
            { name: "to",           type: "address" },
            { name: "deadline",     type: "uint256" },
        ],
        outputs: [],
    },
    {
        type: "function", name: "swapExactTokensForETHSupportingFeeOnTransferTokens",
        stateMutability: "nonpayable",
        inputs: [
            { name: "amountIn",     type: "uint256" },
            { name: "amountOutMin", type: "uint256" },
            { name: "path",         type: "address[]" },
            { name: "to",           type: "address" },
            { name: "deadline",     type: "uint256" },
        ],
        outputs: [],
    },
] as const;

export const RARITY_LABEL = ["Unrevealed", "Piglet", "Yearling", "Hog", "Razorback", "Royal Sow", "Cosmic Piggy"] as const;
export const RARITY_BOOST_BPS = [0, 50, 100, 150, 200, 300, 500] as const;
export const RARITY_COLOR = [
    "from-piggy-cream to-piggy-cream/60",
    "from-pink-200 to-pink-400",
    "from-pink-300 to-pink-500",
    "from-pink-400 to-pink-600",
    "from-pink-500 to-pink-700",
    "from-piggy-gold-400 to-piggy-pink-700",
    "from-piggy-pink-300 via-piggy-gold-400 to-piggy-violet",
] as const;
